import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { KeyIcon, PencilSquareIcon, PlusIcon, PowerIcon } from "@heroicons/react/24/outline";
import DataTable from "../../../components/table/DataTable";
import StatusBadge from "../../../components/table/StatusBadge";
import ActionGroup, { ActionButton } from "../../../components/table/ActionGroup";
import ConfirmDialog from "../../../components/dialog/ConfirmDialog";
import FormModal from "../../../components/modal/FormModal";
import PrimaryButton from "../../../components/button/PrimaryButton";
import PaginatedView from "../../../components/pagination/PaginatedView";
import { activateEmployee, deactivateEmployee, listEmployees, resetEmployeePassword } from "../../../services/employees";

const PAGE_SIZE = 20;

const PasswordForm = ({ onSave, saving }) => {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (password.length < 5) return setError("Password must be at least 5 characters.");
    if (password !== confirmation) return setError("Passwords do not match.");
    setError("");
    onSave(password);
  };

  return <div className="space-y-4">
    {error ? <p className="text-sm text-red-700">{error}</p> : null}
    <PasswordInput label="New Password" value={password} onChange={setPassword} />
    <PasswordInput label="Confirm New Password" value={confirmation} onChange={setConfirmation} />
    <div className="flex justify-end"><PrimaryButton title="Reset Password" onClick={submit} isBusy={saving} /></div>
  </div>;
};

const PasswordInput = ({ label, value, onChange }) => <label className="block text-sm font-semibold text-slate-700">
  {label}
  <input aria-label={label} type="password" value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full rounded border border-slate-300 px-3 py-2" />
</label>;

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [passwordEmployee, setPasswordEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const { page } = useParams();
  const currentPage = Math.max((Number(page) || 1) - 1, 0);

  const loadEmployees = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await listEmployees(currentPage, PAGE_SIZE);
      setEmployees(response?.data ?? []);
      setMetadata(response?.metadata ?? null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEmployees(); }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async () => {
    setSaving(true);
    setError("");
    try {
      if (selected.enabled) await deactivateEmployee(selected.id);
      else await activateEmployee(selected.id);
      setSelected(null);
      await loadEmployees();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (password) => {
    setSaving(true);
    setError("");
    try {
      await resetEmployeePassword(passwordEmployee.id, password);
      setPasswordEmployee(null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: "name", header: "Name", render: (employee) => `${employee.firstName} ${employee.lastName}` },
    { key: "email", header: "Email" },
    { key: "phoneNumber", header: "Phone", render: (employee) => employee.phoneNumber || "—" },
    { key: "address", header: "Address", render: (employee) => employee.address || "—" },
    { key: "enabled", header: "Status", render: (employee) => <StatusBadge active={employee.enabled} /> },
    { key: "actions", header: "Actions", align: "right", render: (employee) => <ActionGroup>
      <ActionButton as={Link} to={`/employees/${employee.id}/edit`} title="Edit" color="blue"><PencilSquareIcon className="h-5 w-5" /></ActionButton>
      <ActionButton onClick={() => setPasswordEmployee(employee)} title="Reset password" color="purple"><KeyIcon className="h-5 w-5" /></ActionButton>
      <ActionButton onClick={() => setSelected(employee)} title={employee.enabled ? "Deactivate" : "Activate"} color={employee.enabled ? "red" : "green"}><PowerIcon className="h-5 w-5" /></ActionButton>
    </ActionGroup> },
  ];

  return <div className="space-y-5">
    <div className="flex items-center justify-between"><h1 className="text-xl font-bold uppercase text-slate-700">Employees</h1><Link to="/employees/create" className="inline-flex items-center gap-1 rounded bg-primary px-4 py-2 text-sm font-bold text-white"><PlusIcon className="h-4 w-4" />Add Employee</Link></div>
    {error ? <div role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
    <DataTable columns={columns} rows={employees} rowKey="id" loading={loading} emptyMessage="No employee accounts found." />
    {metadata?.totalPages > 1 ? <PaginatedView path="/employees/page" total={metadata.totalPages} current={metadata.currentPage} pageSize={metadata.pageSize} currentItemCount={employees.length} /> : null}
    <ConfirmDialog title={`${selected?.enabled ? "Deactivate" : "Activate"} employee?`} message={selected?.enabled ? "This employee will no longer be able to sign in." : "This employee will be able to sign in again."} visible={Boolean(selected)} onAccept={changeStatus} onDismiss={() => setSelected(null)} />
    <FormModal hidden={!passwordEmployee} title={`Reset Password — ${passwordEmployee?.email ?? ""}`} onDismiss={() => setPasswordEmployee(null)}><PasswordForm onSave={resetPassword} saving={saving} /></FormModal>
  </div>;
};

export default EmployeeList;
